import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { EnvironmentConfig, BaseApiService, AuthenticationService, DateTimeUtils } from "../../core/core";
import { Utilization } from "../order";
import { Observable } from "rxjs";
import { GpsInformation } from "../models/gps-information.model";
import { DatePipe } from "@angular/common";

declare var environment: EnvironmentConfig;

@Injectable({ providedIn: "root" })
export class TelematicsService extends BaseApiService<any> {
  constructor(httpClient: HttpClient, authenticationService: AuthenticationService) {
    super(httpClient, environment.ordersApiDomain, "/api/orders/v2/order", true, authenticationService);
  }

  getUtilizationForRentalRequestItem(rentalRequestId: string, inventoryItemId: string) {
    const headers = this.getAuth();

    return this.httpClient.get<Utilization[]>(`${environment.ordersApiDomain}/api/orders/v1/rental/${rentalRequestId}/item/${inventoryItemId}/utilization`, {
      headers,
    });
  }

  getUtilizationForRentalRequests(rentalRequestIds: string[], recordCountPerItem: number = 7): Observable<Utilization[]> {
    const body = {
      rentalRequestIds,
      recordCountPerItem,
    };
    const headers = this.getAuth();
    return this.httpClient.post<Utilization[]>(`${environment.ordersApiDomain}${this.endpoint}/utilizations`, body, { headers });
  }

  getGpsDataForRentalRequests(rentalRequestIds: string[], recordCountPerItem: number = 7): Observable<GpsInformation[]> {
    const body = {
      rentalRequestIds,
    };
    const headers = this.getAuth();
    return this.httpClient.post<GpsInformation[]>(`${environment.ordersApiDomain}${this.endpoint}/locations`, body, { headers });
  }

  getGpsInformationIcons(gpsInformation: GpsInformation) {
    let css = {
      ignitionDetect: "text-grey",
      voltage: "text-grey",
      gpsLocationFresh: "text-grey",
      batteryPercent: "text-grey fa-battery-empty",
      lastUplink: "text-grey",
      lastDownload: "text-grey",
    };

    let title = {
      batteryPercent: "0%",
      lastUpLink: "Last Uplink: N/A",
      voltage: "0.0",
      lastDownload: "Last Download: N/A",
    };

    if (gpsInformation) {
      const ignitionDetect = gpsInformation.sensors.find(d => (d.name = "Ignition Detect"));
      css.ignitionDetect = ignitionDetect != null ? "text-success" : "text-danger";
      if (ignitionDetect?.readings?.length) {
        const voltReading = ignitionDetect?.readings.find(d => d.readingType == "volts");
        if (voltReading) {
          css.voltage = voltReading.value > 11 ? "text-success" : "text-danger";
          title.voltage = voltReading.value.toString();
        }
      }

      css.batteryPercent =
        gpsInformation.status.batteryPercent >= 100
          ? "text-success fa-battery-full"
          : gpsInformation.status.batteryPercent >= 75
          ? "text-success fa-battery-three-quarters"
          : gpsInformation.status.batteryPercent >= 50
          ? "text-success fa-battery-half"
          : gpsInformation.status.batteryPercent >= 25
          ? "text-warning fa-battery-quarter"
          : "text-danger fa-battery-low";

      title.batteryPercent = `${gpsInformation.status.batteryPercent}%`;

      const lastUpLinkHours = DateTimeUtils.hoursDifference(new Date(), DateTimeUtils.verifyDateValue(gpsInformation.status.lastUplink));
      css.lastUplink = lastUpLinkHours > 8 ? "text-danger" : lastUpLinkHours > 2 ? "text-warning" : "text-success";
      title.lastUpLink = `Last Uplink: ${new DatePipe("en").transform(gpsInformation.status.lastUplink, "short")}`;

      const lastRequestedHours = DateTimeUtils.hoursDifference(new Date(), DateTimeUtils.verifyDateValue(gpsInformation.status.requestedAt));
      css.lastDownload = lastUpLinkHours > 8 ? "text-danger" : lastRequestedHours > 2 ? "text-warning" : "text-success";
      title.lastDownload = `Last Download: ${new DatePipe("en").transform(gpsInformation.status.requestedAt, "short")}`;
    }

    return `<div class="d-flex fs-18 align-items-center">
    <i class="icon-key mr-1 ${css.ignitionDetect}" title='Ignition Detect Sensor&#013;Grey: No connection&#013;Red: Ignition Detect Sensor is not added&#013;Green: All good'></i>
    <span class='${css.voltage} mr-1 fs-15'>${title.voltage}<small class='${css.voltage}'>v</small></span>
    <i class="fa-solid ${css.batteryPercent} mr-1" title='Battery: ${title.batteryPercent}'></i>
    <i class="fa-solid fa-upload mr-1 ${css.lastUplink}" title='${title.lastUpLink}&#013;Grey: No Connection&#013;Red: 8+ hours&#013;Yellow: 2+ hours&#013;Green: Last two hours'></i>
    <i class="fa-solid fa-download mr-1 ${css.lastDownload}" title='${title.lastDownload}&#013;Grey: No Connection&#013;Red: 8+ hours&#013;Yellow: 2+ hours&#013;Green: Last hour'></i>
    </div>`;
  }
}
